import type { Dictionary } from './en.ts';

/**
 * Hebrew is the first right-to-left locale here. The strings themselves need
 * nothing special — the direction is a property of the document, set from
 * `directionOf` in the provider — but note that the interpolated values are
 * often left-to-right runs (a timer, a status code, a language name in Latin
 * script). They are wrapped where a bare mix would reorder badly on screen.
 */
export const he: Dictionary = {
  common: {
    tagline: 'מתרגם קולי בזמן אמת',
    interfaceLanguage: 'שפת הממשק',
  },

  login: {
    title: 'כניסה',
    lede: 'הזינו את קוד הגישה להדגמה. הדפדפן יזכור אותו ל־12 שעות.',
    submit: 'כניסה',
    checking: 'בודק…',
    wrong: 'הקוד שגוי. בדקו אותו ונסו שוב.',
    failed: 'הכניסה נכשלה. נסו שוב בעוד רגע.',
  },

  home: {
    title: 'שיחה בשתי שפות',
    lede: 'כל אחד מדבר בשפה שלו ושומע את האחר מתורגם. שני אנשים בחדר, בלי הרשמה.',
    myLanguage: 'השפה שלי',
    peerLanguage: 'השפה שלהם',
    accessCode: 'קוד גישה להדגמה',
    signOut: 'יציאה',
    samePair: 'בחרו שתי שפות שונות — אחרת אין מה לתרגם.',
    create: 'יצירת חדר',
    creating: 'יוצר…',
    createFailed: 'לא הצלחנו ליצור את החדר',

    readyTitle: 'החדר מוכן',
    youHear: (language: string) => `אתם שומעים: ⁨${language}⁩.`,
    theyHear: (language: string) => `הם שומעים: ⁨${language}⁩.`,
    inviteLabel: 'קישור הזמנה — שלחו אותו לאדם השני',
    copy: 'העתקה',
    copied: 'הועתק',
    inviteNote:
      'הקישור תקף ל־15 דקות ומכניס בדיוק אדם אחד. אל תפרסמו אותו בשום מקום — הוא המפתח לחדר.',
    sourceSubtitles: 'להציג גם את המילים המקוריות',
    sourceSubtitlesOn:
      'תראו מה כל אחד באמת אמר, בשפה שלו, לצד התרגום. הספק גובה על כך בנפרד, ולכן השיחה יקרה יותר.',
    sourceSubtitlesOff: 'מוצג רק התרגום. שום דבר לא מתומלל בשפת המקור ואין תוספת תשלום.',
    sourceSubtitlesReady: 'גם המילים המקוריות יוצגו.',
    goToCheck: 'בדיקת השמע שלי',
  },

  voice: {
    you: 'אתם',
    peer: 'האדם השני',
    language: 'שפה',
    provider: 'הקראה',
    openaiNote: 'קול מובנה, נשמע מיד',
    elevenlabsNote: 'קול נקי יותר, מתחיל בסוף המשפט',
    noVoices: 'אין קולות לשפה הזו',
    voice: 'קול',
    builtIn: 'הקול המובנה של OpenAI',
    loading: 'טוען קולות…',
    failed: 'לא הצלחנו לטעון את הקולות. נסו שוב מאוחר יותר.',
    hintBuiltIn: 'קול אחד לכל השפות, כמו קודם.',
    hintLibrary: 'קול מהספרייה של ElevenLabs.',
    groupVerified: 'נבדקו לשפה הזו',
    groupOther: 'קולות אחרים',
    accent: 'הקול הזה לא נבדק לשפה הזו, וייתכן שיישמע במבטא.',
    noSample: 'לקול הזה אין דוגמה',
    listen: 'האזנה',
    stop: 'עצירה',
    needVoice: 'בחרו קול לכל צד עם ElevenLabs, או החזירו אותו ל־OpenAI.',
  },

  join: {
    checking: 'בודק את ההזמנה…',
    title: 'הזמנה לשיחה',
    youWillHear: (language: string) => `אתם תשמעו: ⁨${language}⁩.`,
    theyHear: (language: string) => `הם שומעים: ⁨${language}⁩.`,
    connect: 'הצטרפות',
    connecting: 'מצטרף…',
    full: 'החדר מלא.',
    failedTitle: 'לא הצלחנו להיכנס',
    createYourOwn: 'צרו חדר משלכם',
    noInvite: 'אין הזמנה בקישור הזה. בקשו אותו שוב.',
    unavailable: 'החדר הזה אינו זמין',
    joinFailed: 'ההצטרפות נכשלה',
  },

  check: {
    title: 'בדיקת שמע',
    lede: 'הרכיבו אוזניות: בלעדיהן התרגום חוזר אל המיקרופון שלכם והאדם השני שומע הד.',
    microphone: 'מיקרופון',
    defaultMic: 'מיקרופון ברירת מחדל',
    level: 'עוצמה — אמרו משהו',
    applied: (features: string) => `הדפדפן שלכם הפעיל: ⁨${features}⁩.`,
    appliedNone: 'ללא עיבוד',
    echoCancellation: 'ביטול הד',
    noiseSuppression: 'הפחתת רעש',
    autoGain: 'הגברה אוטומטית',
    measureNote: 'מדדו כשהאוזניות עליכם.',
    testSound: 'בדיקת הצליל',
    testAgain: 'בדיקת הצליל שוב',
    start: 'נשמע טוב, מתחילים',
    starting: 'מתחבר…',
    micDenied: 'הגישה למיקרופון נדחתה. אפשרו אותה בהגדרות הדפדפן וטענו מחדש את הדף.',
    micNotFound: 'לא נמצא מיקרופון. חברו אחד וטענו מחדש את הדף.',
    micFailed: 'לא הצלחנו לפתוח את המיקרופון.',
  },

  call: {
    title: 'שיחה',
    timeLeft: (time: string) => `· נותרו ⁨${time}⁩`,
    waitingPeer: 'ממתינים לאדם השני. שלחו לו את קישור ההזמנה.',
    autoplayBlocked: 'הדפדפן שלכם חסם את השמעת הקול.',
    enableSound: 'הפעלת הקול',
    lagging: 'התרגום מפגר אחרי הדיבור. אפשר להפעיל מחדש את המתרגם — הקטע הלא גמור יאבד.',
    translatorStopped: (reason: string) =>
      `התרגום נעצר: ⁨${reason}⁩. השיחה נמשכת — הפעילו מחדש את המתרגם.`,
    translationVolume: 'עוצמת התרגום',
    hearOriginal: 'לשמוע את הקול המקורי',
    muteMic: 'כיבוי המיקרופון',
    unmuteMic: 'הפעלת המיקרופון',
    restart: 'הפעלה מחדש של התרגום',
    asleep:
      'אף אחד לא דיבר במשך דקה, ולכן התרגום הושהה כדי לחסוך בעלויות. הוא יחזור מעצמו כשבן או בת השיח ידברו.',
    wake: 'להמשיך עכשיו',
    leave: 'יציאה',
    peerMuted: 'האדם השני כיבה את המיקרופון',

    metricConnect: 'הפעלת המתרגם',
    metricFirst: 'התרגום הראשון',
    metricLag: 'פיגור משוער',
    metricRtt: 'זמן הלוך־ושוב לספק',
    metricVoiceDelay: 'עיכוב ההקראה',
    metricCost: 'עלות עד עכשיו (הערכה)',
    costBreakdown: (openai: string, voice: string, credits: number) =>
      `OpenAI ${openai} · ElevenLabs ${voice} · קרדיטים: ${credits}`,
    costNote: 'מחירים פומביים מהגדרות השרת, לא החשבונית של הספקים.',
    metricVoiceLead: 'ElevenLabs מול OpenAI, ממוצע',
    voiceLeadEarlier: (seconds: string) => `מוקדם יותר ב־⁨${seconds}⁩ שנ׳`,
    voiceLeadLater: (seconds: string) => `מאוחר יותר ב־⁨${seconds}⁩ שנ׳`,
    iHear: (voice: string) => `אתם שומעים: ⁨${voice}⁩`,
    peerHears: (voice: string) => `הם שומעים: ⁨${voice}⁩`,
    voiceFellBack: 'OpenAI (ElevenLabs לא הגיב)',

    endedTitle: 'השיחה הסתיימה',
    endedNewRoom: 'לשיחה חדשה, צרו חדר חדש.',
    newRoom: 'חדר חדש',
    rateTitle: 'דרגו את השיחה',
  },

  closed: {
    'peer-left': 'האדם השני סיים את השיחה.',
    'time-limit': 'הגענו למגבלת הזמן של ההדגמה.',
    'peer-lost': 'האדם השני נעדר יותר מדי זמן.',
    'server-shutdown': 'השרת מופעל מחדש.',
    expired: 'זמן החדר פג.',
    unknown: 'החיבור נסגר.',
    signalLost: 'איבדנו קשר עם השרת. צרו חדר חדש.',
  },

  status: {
    serverConnecting: 'שרת: מתחבר',
    serverOpen: 'שרת: מחובר',
    serverReconnecting: 'שרת: מתחבר מחדש',
    serverClosed: 'שרת: מנותק',

    peerWaiting: 'הצד השני: ממתין',
    peerConnecting: 'הצד השני: מתחבר',
    peerConnected: 'הצד השני: מחובר',
    peerReconnecting: 'הצד השני: מתאושש',
    peerFailed: 'הצד השני: אין חיבור',
    peerGone: 'הצד השני: יצא',

    translatorIdle: 'תרגום בשבילי: לא התחיל',
    translatorConnecting: 'תרגום בשבילי: מתחיל',
    translatorReady: 'תרגום בשבילי: פועל',
    translatorReconnecting: 'תרגום בשבילי: מתאושש',
    translatorFailed: 'תרגום בשבילי: נכשל',
    translatorClosed: 'תרגום בשבילי: נעצר',
    translatorAsleep: 'תרגום בשבילי: מושהה',

    peerTranslatorReady: 'תרגום בשבילם: פועל',
    peerTranslatorWaiting: 'תרגום בשבילם: ממתין',
    peerTranslatorAsleep: 'תרגום בשבילם: מושהה',
    relayed: 'מסלול: דרך TURN',
  },

  subtitles: {
    them: 'הם',
    you: 'אתם',
    themEmpty: 'הם עוד לא אמרו כלום',
    youEmpty: 'אמרו משהו — המילים שלכם והתרגום שלהן יופיעו כאן',
  },

  rating: {
    accuracy: 'דיוק התרגום',
    voice: 'כמה הקול נשמע טבעי',
    comfort: 'כמה השיחה הייתה נוחה',
    send: 'שליחת הדירוגים',
    thanks: 'תודה — הדירוגים נשמרו.',
  },

  errors: {
    no_credit: 'נגמר היתרה בחשבון OpenAI — טענו אותו',
    rate_limited: 'שירות התרגום מגביל אותנו — נסו שוב בעוד דקה',
    unconfigured: 'בשרת לא מוגדר מפתח תרגום',
    unreachable: 'השרת לא הצליח להגיע לשירות התרגום',
    offline: 'לא הצלחנו להגיע לשרת — בדקו את החיבור',
    session_expired: 'תוקף הפעלת התרגום פג — הפעילו את התרגום מחדש',
    room_closed: 'החדר הזה כבר סגור',
    bad_offer: 'הדפדפן הפיק הצעת חיבור לא תקינה',
    provider_refused: (status: number) => `שירות התרגום סירב לחיבור (⁨${status}⁩)`,
    unknown: 'לא הצלחנו להתחיל את התרגום',
  },

  banner: {
    providerUnconfigured: 'בשרת לא מוגדר מפתח לספק תרגום. אפשר ליצור חדר, אבל שום דבר לא יתורגם.',
  },
};
